import {
  ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid,
} from "recharts";
import { DeviceMobileIcon, GlobeIcon } from "@phosphor-icons/react";
import ScanMap from "@/components/ScanMap";
import { Badge } from "@/components/ui/badge";

function Panel({ title, children, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-6">
      {title && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">{title}</p>}
      {children}
    </div>
  );
}

function BreakdownList({ data }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (!data.length) return <p className="font-mono text-xs text-white/30">No data yet</p>;
  return (
    <div className="space-y-3">
      {data.map((d) => (
        <div key={d.name}>
          <div className="flex justify-between font-mono text-xs mb-1">
            <span className="text-white/70 truncate pr-2">{d.name}</span>
            <span className="text-primary">{d.value}</span>
          </div>
          <div className="h-1.5 bg-secondary/60"><div className="h-full bg-primary/70" style={{ width: `${(d.value / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function StatCell({ label, value, color = "" }) {
  return (
    <div className="p-6 border-r border-border last:border-r-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</p>
      <p className={`font-mono text-4xl mt-2 ${color}`}>{value ?? "—"}</p>
    </div>
  );
}

export default function PinAnalytics({ stats }) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 border border-border bg-card mb-6" data-testid="pin-analytics-stats">
        <StatCell label="Total scans" value={stats?.total_scans} color="text-primary" />
        <StatCell label="Unique" value={stats?.unique_visitors} />
        <StatCell label="Repeat" value={stats?.repeat_visitors} />
        <StatCell label="Link clicks" value={stats?.total_clicks} />
        <StatCell label="In-app" value={stats?.in_app_scans} color="text-accent" />
      </div>

      <div className="space-y-6">
        <Panel title="// Scans · last 14 days" testid="pin-analytics-timeseries">
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.timeseries || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#666", fontFamily: "JetBrains Mono", fontSize: 9 }} tickFormatter={(d) => d.slice(5)} axisLine={{ stroke: "#1f1f1f" }} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(0,255,102,0.06)" }} contentStyle={{ background: "#0a0a0a", border: "1px solid #1f1f1f", fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#00ff66" }} />
                <Bar dataKey="scans" fill="#00ff66" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="// Scan map" testid="pin-analytics-map">
          <div className="flex items-center justify-between mb-4 -mt-1">
            <span className="font-mono text-[11px] text-white/40">Where this pin is scanned</span>
            <span className="font-mono text-[11px] text-primary">{stats?.located_scans ?? 0} located</span>
          </div>
          <ScanMap points={stats?.map_points || []} />
        </Panel>

        <div className="grid sm:grid-cols-2 gap-6">
          <Panel title="// By country" testid="pin-analytics-country"><BreakdownList data={stats?.by_country || []} /></Panel>
          <Panel title="// By city" testid="pin-analytics-city"><BreakdownList data={stats?.by_city || []} /></Panel>
          <Panel title="// By device" testid="pin-analytics-device"><BreakdownList data={stats?.by_device || []} /></Panel>
          <Panel title="// By OS" testid="pin-analytics-os"><BreakdownList data={stats?.by_os || []} /></Panel>
          <Panel title="// By browser" testid="pin-analytics-browser"><BreakdownList data={stats?.by_browser || []} /></Panel>
          <Panel title="// In-app browser share" testid="pin-analytics-inapp"><BreakdownList data={stats?.by_in_app || []} /></Panel>
        </div>

        <Panel title="// Recent scans" testid="pin-analytics-recent">
          {(stats?.recent || []).length === 0 ? (
            <p className="font-mono text-xs text-white/30">No scans recorded yet.</p>
          ) : (
            <div className="-mx-2">
              {stats.recent.map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-2 py-2.5 border-b border-border/60 last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {s.device === "Mobile" ? <DeviceMobileIcon className="text-white/40 shrink-0" /> : <GlobeIcon className="text-white/40 shrink-0" />}
                    <span className="font-mono text-xs text-white/70 truncate">
                      {[s.device, s.os, s.browser].filter(Boolean).join(" · ") || "Unknown"}{s.city ? ` · ${s.city}` : ""}{s.country ? `, ${s.country}` : ""}
                    </span>
                    {s.in_app_browser && <Badge variant="outline" className="font-mono text-[9px] border-accent/40 text-accent shrink-0">{s.in_app_name}</Badge>}
                  </div>
                  <span className="font-mono text-[11px] text-white/30 shrink-0">{new Date(s.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <p className="font-mono text-[10px] text-white/30">Visitor IPs are hashed for privacy (POPIA/GDPR) — never stored raw. Location is best-effort, city-level.</p>
      </div>
    </>
  );
}
